import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/components/grc/shared";
import { SEVERITY_BADGE } from "@/components/grc/FindingsRegister";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const STATUSES = ["Todo", "InProgress", "Blocked", "Done"];
const UNASSIGNED = "__unassigned__";

export const TaskDetailDrawer = ({ task, open, canMutate, onClose, onChanged }) => {
  const [detail, setDetail] = useState(task);
  const [assignees, setAssignees] = useState([]);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDetail(task);
    setComment("");
  }, [task]);

  useEffect(() => {
    if (open) {
      api.get("/grc/assignees").then(({ data }) => setAssignees(data.assignees || [])).catch(() => {});
    }
  }, [open]);

  const mutate = async (body, successMsg) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/grc/remediation-tasks/${detail.task_id}`, body);
      setDetail((d) => ({ ...d, ...data }));
      toast.success(successMsg);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!detail) return null;

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="task-detail-drawer">
        <SheetHeader>
          <SheetTitle className="text-base text-[#003366] text-left leading-snug">
            {detail.finding_title || detail.description || "Remediation task"}
          </SheetTitle>
          <SheetDescription className="text-xs text-left">
            Remediation task detail and activity
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            {detail.severity && (
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${SEVERITY_BADGE[detail.severity] || "bg-slate-200"}`}>
                {detail.severity}
              </span>
            )}
            {detail.pillar && (
              <span className="px-2 py-0.5 rounded border border-slate-300 text-[10px] font-mono text-slate-600">
                {detail.pillar}
              </span>
            )}
            <span className="text-[11px] text-slate-500">Due {fmtDate(detail.due_date)}</span>
            {detail.escalation_level > 0 && (
              <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold">
                Escalation L{detail.escalation_level}
              </span>
            )}
          </div>

          {detail.description && (
            <p className="text-xs text-slate-600 leading-relaxed border-l-2 border-slate-200 pl-3">
              {detail.description}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select
                value={detail.status}
                onValueChange={(v) => mutate({ status: v }, `Status set to ${v}`)}
                disabled={!canMutate || saving}
              >
                <SelectTrigger className="h-9 text-xs" data-testid="task-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Assignee</Label>
              <Select
                value={detail.assignee_id || UNASSIGNED}
                onValueChange={(v) => mutate({ assignee_id: v === UNASSIGNED ? "" : v }, "Assignee updated")}
                disabled={!canMutate || saving}
              >
                <SelectTrigger className="h-9 text-xs" data-testid="task-assignee-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {assignees.map((a) => (
                    <SelectItem key={a.username} value={a.username}>{a.username} ({a.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Comments ({(detail.comments || []).length})</Label>
            <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="task-comments-list">
              {(detail.comments || []).length === 0 ? (
                <p className="text-[11px] text-slate-400">No comments yet.</p>
              ) : (
                detail.comments.map((c) => (
                  <div key={c.comment_id} className="rounded-lg bg-slate-50 border border-slate-100 p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-semibold text-[#003366]">{c.author}</span>
                      <span className="text-[9px] text-slate-400">{fmtDateTime(c.at)}</span>
                    </div>
                    <p className="text-[11px] text-slate-600">{c.text}</p>
                  </div>
                ))
              )}
            </div>
            {canMutate && (
              <div className="space-y-2">
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment…"
                  className="text-xs min-h-[60px]"
                  data-testid="task-comment-input"
                />
                <Button
                  size="sm"
                  disabled={!comment.trim() || saving}
                  onClick={() => {
                    mutate({ comment }, "Comment added");
                    setComment("");
                  }}
                  className="bg-[#008080] hover:bg-[#1a51db] h-8 text-xs"
                  data-testid="task-comment-submit"
                >
                  Add Comment
                </Button>
              </div>
            )}
          </div>

          <p className="text-[10px] text-slate-400 border-t border-slate-100 pt-2">
            Created {fmtDateTime(detail.created_at)}
            {detail.updated_at && <> · Updated {fmtDateTime(detail.updated_at)}</>}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
